/**
 * PostHog for the public onboarding page — a plain web page with NO `chrome.*`
 * (unlike src/lib/analytics.ts). It runs in two places and picks its ingest
 * endpoint by host:
 *
 * - On the RU VPS (`r.mrstanis.ru`): sends SAME-ORIGIN to `/ph/…`, which nginx
 *   reverse-proxies to PostHog. PostHog's own host is Cloudflare-fronted and
 *   throttled from inside RU, so a direct call would silently drop the event —
 *   the whole reason this page (and its install signal) had to leave Cloudflare.
 * - On Cloudflare Pages (non-RU): sends DIRECT to PostHog.
 *
 * The service worker appends `did` (the device distinct_id), `v` (extension
 * version) and `lang` (UI locale) to the welcome URL on install, so this
 * page's event joins the same person/timeline as the SW's `extension_installed`.
 *
 * Fire-and-forget, `keepalive`, `text/plain` body (a CORS-safelisted content
 * type → no preflight on the cross-origin Cloudflare path; the same-origin
 * proxy path needs none anyway). Everything is best-effort: a failed send
 * never throws.
 */
import {
  POSTHOG_CAPTURE_PATH,
  POSTHOG_EVENTS,
  POSTHOG_HOST,
} from '../shared/analytics-config';

// Host that fronts the same-origin PostHog reverse-proxy (nginx `location /ph/`).
const RU_PROXY_HOST = 'r.mrstanis.ru';

const API_KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim() ?? '';

/** `${origin}/ph` when served from the RU VPS, else PostHog directly. */
function captureUrl(): string {
  const onRuVps =
    typeof location !== 'undefined' && location.hostname === RU_PROXY_HOST;
  const base = onRuVps ? `${location.origin}/ph` : POSTHOG_HOST;
  return `${base}${POSTHOG_CAPTURE_PATH}`;
}

/**
 * Fire `welcome_viewed` once on page load. `did` (distinct_id) comes from the
 * URL; if absent (page opened directly, not via the install redirect) we mint a
 * throwaway id so the install still counts, just without a per-device join.
 */
export function trackWelcomeViewed(): void {
  if (API_KEY.length === 0) return;
  try {
    const params = new URLSearchParams(location.search);
    const distinctId = params.get('did') || crypto.randomUUID();
    const payload = {
      api_key: API_KEY,
      event: POSTHOG_EVENTS.welcomeViewed,
      distinct_id: distinctId,
      properties: {
        extension_version: params.get('v') ?? undefined,
        locale: params.get('lang') ?? undefined,
        $lib: 'welcome-page',
        host: location.hostname,
      },
      timestamp: new Date().toISOString(),
    };
    void fetch(captureUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Best-effort: a throttled/blocked PostHog must never break onboarding.
    });
  } catch {
    // URL/crypto unavailable in some exotic context — skip silently.
  }
}
